import { computed } from 'vue';
import { type useUserStore } from '@/stores/user';

// Define the keys that can be accessed on MinimalCompany
type CompanyFlagKey =
  | 'isFhdnAffiliateEnabled'
  | 'isFhdnApiAffiliateEnabled'
  | 'isFhdnApiProviderEnabled'
  | 'isFhdnProviderEnabled';

export function useUserRoles(userStore: ReturnType<typeof useUserStore>) {
  // Helper function to create computed properties for company flags
  function companyFlag(key: CompanyFlagKey) {
    return computed(() => userStore.user?.company[key] ?? false);
  }

  // Whether the company is enabled as a regular FHDN affiliate (can resell FHDN products)
  const isFhdnAffiliateEnabled = companyFlag('isFhdnAffiliateEnabled');

  // Whether the company is enabled as an API affiliate (can access FHDN API)
  const isFhdnApiAffiliateEnabled = companyFlag('isFhdnApiAffiliateEnabled');

  // Whether the company is enabled as an API provider (can provide products via API)
  const isFhdnApiProviderEnabled = companyFlag('isFhdnApiProviderEnabled');

  // Whether the company is enabled as a regular FHDN provider (can list products on FHDN)
  const isFhdnProviderEnabled = companyFlag('isFhdnProviderEnabled');

  // Whether the company supplies products (either via API or regular FHDN)
  const isSupplier = computed(
    () => isFhdnProviderEnabled.value || isFhdnApiProviderEnabled.value,
  );

  // Whether the company is a non-API affiliate
  const isFhdnAffiliate = computed(
    () => isFhdnAffiliateEnabled.value && !isFhdnApiAffiliateEnabled.value,
  );

  // Whether the user has access to FHDN features
  const inFhdn = computed(() => userStore.user?.inFhdn || false);

  // Whether the user has access to FHDN API features
  const inFhdnApi = computed(() => userStore.user?.inFhdnApi || false);

  return {
    isFhdnAffiliateEnabled,
    isFhdnApiAffiliateEnabled,
    isFhdnApiProviderEnabled,
    isFhdnProviderEnabled,
    isSupplier,
    isFhdnAffiliate,
    inFhdn,
    inFhdnApi,
  };
}
